/**
 * Admin deal-draft helpers — VDR onboard capture (no passwords in git).
 * Persistence: admin API under ENERGY_AUTH_DATA, with localStorage + export fallback.
 */

export const LOCAL_DRAFTS_KEY = 'd2d.energy.dealDrafts';

export const PORTAL_TYPES = [
  'Datarooms.com',
  'Box',
  'Intralinks',
  'Datasite',
  'Firmex',
  'other',
];

export const AUTH_METHODS = ['sso', 'password', 'invite-link', 'unknown'];

export const PACKAGE_EXPECTATIONS = [
  { id: 'aries', label: 'ARIES database / Access DB' },
  { id: 'phdwin', label: 'PHDWin' },
  { id: 'oneline', label: 'Oneline xlsx' },
  { id: 'land', label: 'Land / lease schedule' },
  { id: 'maps', label: 'Maps / shapefiles' },
  { id: 'logs', label: 'Well logs / LAS' },
  { id: 'financials', label: 'Financials / CF' },
];

/**
 * Propose `{name}-{basin}-{year}` slug from free text.
 * Accepts `label` as an alias for `name` (wizard form / draftToDealStub shape).
 */
export function proposeDealIdSlug({ name = '', label = '', basin = '', year } = {}) {
  const y = year || new Date().getFullYear();
  const slugPart = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32);
  const n = slugPart(name || label) || 'deal';
  const b = slugPart(basin) || 'basin';
  return `${n}-${b}-${y}`;
}

/** Propose a 2–4 letter mark from the deal name. */
export function proposeMark(name = '') {
  const words = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return 'NEW';
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words
    .slice(0, 3)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

export function emptyDraftForm() {
  return {
    label: '',
    basin: '',
    region: '',
    commodity: 'oil',
    primaryProduct: 'oil',
    dealId: '',
    mark: '',
    shortLabel: '',
    subtitle: '',
    seller: '',
    advisor: 'RedOaks Energy Advisors',
    bidsDueLabel: '',
    asOfLabel: '',
    portal: {
      type: 'Datarooms.com',
      url: '',
      roomName: '',
      entitledEmail: '',
      authMethod: 'unknown',
      secretRef: '',
      accessNotes: '',
    },
    packagesExpected: [],
    pasteGrantEmail: '',
  };
}

/**
 * Best-effort parse of an access-grant email into portal fields.
 * Never captures password values — only method hints.
 */
export function parseAccessGrantEmail(text) {
  const raw = String(text || '');
  const out = {};
  const urlMatch = raw.match(/https?:\/\/[^\s<>"')\]]+/i);
  if (urlMatch) out.url = urlMatch[0].replace(/[.,;]+$/, '');

  const emailMatch = raw.match(
    /(?:entitled|registered|username|login|user)\s*(?:email)?\s*[:\-]?\s*([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})/i,
  ) || raw.match(/\b([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})\b/i);
  if (emailMatch) out.entitledEmail = emailMatch[1];

  const roomMatch = raw.match(
    /(?:room|data\s*room|workspace|project)\s*(?:name)?\s*[:\-]\s*["']?([^\n"']{3,80})/i,
  );
  if (roomMatch) out.roomName = roomMatch[1].trim();

  const lower = raw.toLowerCase();
  if (/\bss[o0]\b|single sign[- ]on|okta|azure ad/.test(lower)) out.authMethod = 'sso';
  else if (/password|temporary password|passcode/.test(lower)) out.authMethod = 'password';
  else if (/invite link|magic link|click (?:here|the link)/.test(lower)) out.authMethod = 'invite-link';

  if (/intralinks/i.test(raw)) out.type = 'Intralinks';
  else if (/datasite/i.test(raw)) out.type = 'Datasite';
  else if (/firmex/i.test(raw)) out.type = 'Firmex';
  else if (/\bbox\b/i.test(raw)) out.type = 'Box';
  else if (/datarooms?\.com/i.test(raw)) out.type = 'Datarooms.com';

  const notes = [];
  if (out.authMethod === 'password') {
    notes.push('Password auth mentioned — do not store the password; use secretRef if vaulted.');
  }
  if (out.authMethod === 'sso') notes.push('SSO mentioned in grant email.');
  if (notes.length) out.accessNotes = notes.join(' ');

  return out;
}

/** Build a stub deal object suitable for the SPA registry overlay. */
export function draftToDealStub(form) {
  const dealId = (form.dealId || proposeDealIdSlug(form)).trim();
  const label = (form.label || 'Untitled draft').trim();
  const basin = (form.basin || form.region || '').trim();
  const commodity = (form.commodity || 'oil').trim().toLowerCase();
  const mark = (form.mark || proposeMark(label)).trim().toUpperCase().slice(0, 4);
  const shortLabel = (form.shortLabel || label.split(/\s+/)[0] || 'Draft').trim();
  const subtitle =
    form.subtitle?.trim() ||
    [commodity, basin, form.seller].filter(Boolean).join(' · ') ||
    'Draft deal stub';

  return {
    dealId,
    label,
    shortLabel,
    mark,
    subtitle,
    packageName: form.packageName || `${label} — draft stub`,
    seller: form.seller || '',
    advisor: form.advisor || '',
    commodity,
    primaryProduct: (form.primaryProduct || commodity.split('+')[0] || 'oil').trim(),
    legacyFlat: false,
    dataPrefix: dealId,
    extractDir: `scripts/deals/${dealId}`,
    hostExtractRel: `${dealId}/deal-package.json`,
    bidsDueLabel: form.bidsDueLabel || null,
    footerEntity: form.seller || label,
    footerPackage: label,
    draft: true,
    region: form.region || basin || undefined,
    vdrOnboard: {
      asOfLabel: form.asOfLabel || null,
      portal: { ...(form.portal || {}) },
      packagesExpected: [...(form.packagesExpected || [])],
      status: 'draft',
      updatedAt: new Date().toISOString(),
    },
  };
}

/** Markdown onboard brief matching VDR onboard capture fields (no passwords). */
export function exportOnboardBriefMarkdown(stub) {
  const p = stub?.vdrOnboard?.portal || {};
  const pkgs = stub?.vdrOnboard?.packagesExpected || [];
  const lines = [
    `# VDR onboard brief — ${stub.label || stub.dealId}`,
    '',
    '## Deal basics',
    `- **dealId:** \`${stub.dealId}\``,
    `- **label:** ${stub.label}`,
    `- **mark:** ${stub.mark}`,
    `- **commodity:** ${stub.commodity}`,
    `- **primaryProduct:** ${stub.primaryProduct}`,
    `- **region/basin:** ${stub.region || '—'}`,
    `- **seller:** ${stub.seller || '—'}`,
    `- **advisor:** ${stub.advisor || '—'}`,
    `- **bids due:** ${stub.bidsDueLabel || '—'}`,
    `- **as-of:** ${stub.vdrOnboard?.asOfLabel || '—'}`,
    '',
    '## VDR / portal',
    `- **portal type:** ${p.type || '—'}`,
    `- **portal URL:** ${p.url || '—'}`,
    `- **room name:** ${p.roomName || '—'}`,
    `- **entitled email:** ${p.entitledEmail || '—'}`,
    `- **auth method:** ${p.authMethod || '—'} (never store raw passwords in git)`,
    `- **secret ref:** ${p.secretRef || '—'}`,
    `- **access notes:** ${p.accessNotes || '—'}`,
    '',
    '## Packages expected',
    pkgs.length ? pkgs.map((x) => `- ${x}`).join('\n') : '- (none listed)',
    '',
    '## Next ops steps',
    '1. Register `dealId` in `src/deals/deals.js` + `scripts/pipeline/packages.json` when ready to extract.',
    '2. Capture vault paths on Mac; do not commit CA extracts.',
    '3. Run pipeline extract → generate → validate → publish for this deal only.',
    '',
    `_Draft stub · ${stub.vdrOnboard?.updatedAt || new Date().toISOString()}_`,
  ];
  return lines.join('\n');
}

export function readLocalDrafts() {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(LOCAL_DRAFTS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeLocalDrafts(drafts) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(LOCAL_DRAFTS_KEY, JSON.stringify(drafts));
  } catch {
    /* ignore */
  }
}

export function upsertLocalDraft(stub) {
  const list = readLocalDrafts().filter((d) => d.dealId !== stub.dealId);
  list.push(stub);
  writeLocalDrafts(list);
  return list;
}

export function downloadTextFile(filename, text, mime = 'text/markdown') {
  if (typeof document === 'undefined') return;
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
